import * as hostService from '@/services/host/hostService'
import Vue from 'vue'

const state = {
  host: {},
  hostAccessControlHistory: [],
  hosts: [],
  searchTextHosts: [],
  hostSearchResults: [],
  hostServiceTypes: [],
  createHostSuccess: false,
  hostAppointments: [],
  hostPayments: [],
  isLoadingChangeStatus: false,
  isSearchingHosts: false,
  hostHistoryList: [],
  hostLoading: false,
  specialties: [],
  hostSpecialties: [],
  hostRealEstateBlacklist: [],
}

const getters = {
  host: (state) => state.host,
  hostAccessControlHistory: (state) => state.hostAccessControlHistory,
  hosts: (state) => state.hosts,
  hostServiceTypes: (state) => state.hostServiceTypes,
  createHostSuccess: (state) => state.createHostSuccess,
  hostAppointments: (state) => state.hostAppointments,
  searchTextHosts: (state) => state.searchTextHosts,
  hostSearchResults: (state) => state.hostSearchResults,
  hostPayments: (state) => state.hostPayments,
  isLoadingChangeStatus: (state) => state.isLoadingChangeStatus,
  isSearchingHosts: (state) => state.isSearchingHosts,
  hostHistoryList: (state) => state.hostHistoryList,
  hostLoading: (state) => state.hostLoading,
  specialties: (state) => state.specialties,
  hostSpecialties: (state) => state.hostSpecialties,
  hostRealEstateBlacklist: (state) => state.hostRealEstateBlacklist,
}

const actions = {
  async fetchHost({ commit, dispatch }, id) {
    commit('setHostLoading', true)
    commit('setHost', {})
    await hostService
      .find(id)
      .then(({ data }) => {
        commit('setHost', data)
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
      .finally(() => {
        commit('setHostLoading', false)
      })
  },

  async fetchHosts({ rootState, dispatch, commit }, overrideParams) {
    commit('setIsLoading', true)

    const defaultParams = {
      size: rootState.paginate.itemsPerPage,
      page: rootState.paginate.currentPage - 1,
    }

    const params = overrideParams !== undefined
      ? { ...defaultParams, ...overrideParams }
      : { ...defaultParams, ...rootState.filters.filters }

    await hostService
      .findAll(params)
      .then(({ data }) => {
        const { result, total } = data

        commit('setHosts', result)
        commit('setTotalPages', total)
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

  async fetchHostServiceTypes({ dispatch, commit }) {
    commit('setIsLoading', true)

    await hostService
      .listServiceTypes()
      .then(({ data }) => {
        commit('setHostServiceTypes', data)
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

  async fetchHostAccessControlHistory({ dispatch, commit }, id) {
    commit('setIsLoading', true)

    await hostService
      .findHostAccessControlHistory(id)
      .then(({ data }) => {
        commit('setHostAccessControlHistory', data)
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

  async fetchHostPayments({ rootState, dispatch, commit }, hostId) {
    commit('setIsLoading', true)

    const defaultParams = {
      hostId,
      size: rootState.paginate.itemsPerPage,
      page: rootState.paginate.currentPage - 1,
    }

    const params = { ...defaultParams, ...rootState.filters.filters }

    await hostService
      .listHostPayments(params)
      .then(({ data }) => {
        const { result, total } = data

        commit('setHostPayments', result)
        commit('setTotalPages', total)
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

  async createHostPayment({ dispatch, commit }, body) {
    commit('setIsLoading', true)

    await hostService
      .hostPaymentCreate(body)
      .then(() => {
        dispatch('fetchHostPayments', body.host_id)
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

  async linkHostAsaasAccount({ dispatch, commit }, body) {
    commit('setIsLoading', true)

    await hostService
      .linkHostAsaasAccount(body)
      .then(() => {
        dispatch('fetchHosts')
        commit('closeAllModals')
        dispatch('showSuccessMessage', Vue.i18n.translate('host.message.successfullyLinkedAsaasAccount'))
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

  async unlinkHostAsaasAccount({ dispatch, commit }, body) {
    commit('setIsLoading', true)

    await hostService
      .unlinkHostAsaasAccount(body)
      .then(() => {
        dispatch('fetchHosts')
        commit('closeAllModals')
        dispatch('showSuccessMessage', Vue.i18n.translate('host.message.successfullyUnlinkedAsaasAccount'))
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

  async createHost(state, body) {
    return hostService.create(body)
  },

  async createHostOnChecklist({ commit, dispatch }, body) {
    commit('setIsLoading', true)

    await hostService
      .createHostOnChecklist(body)
      .then(() => {
        dispatch('fetchHosts')
        commit('closeAllModals')
        dispatch('showSuccessMessage', Vue.i18n.translate('userChecklistModal.successfullyCreated'))
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

  async updateHost(state, { body, id }) {
    return hostService.update(body, id)
  },

  async suspendHost({ dispatch }, body) {
    return hostService.suspend(body)
  },

  async unsuspendHost({ dispatch }, body) {
    return hostService.unsuspend(body)
  },

  async blockHost({ dispatch }, body) {
    return hostService.block(body)
  },

  async unblockHost({ dispatch }, body) {
    return hostService.unblock(body)
  },

  async deleteHost({ dispatch }, id) {
    await hostService
      .remove(id)
      .then(({ status }) => {
        setTimeout(() => {
          status === 200 &&
            dispatch('showSuccessMessage', 'Host removido com sucesso!')
        }, 500)
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
  },

  fetchHostAppointmentsList({ rootState, dispatch, commit }) {
    commit('setIsLoading', true)

    const defaultParams = {
      size: rootState.paginate.itemsPerPage,
      page: rootState.paginate.currentPage - 1,
    }

    const params = { ...defaultParams, ...rootState.filters.filters }

    hostService
      .listHostAppointments(params)
      .then(({ data }) => {
        const { result, total } = data

        commit('setHostAppointments', result)
        commit('setTotalPages', total)
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

  // payload: the search text, or { text, notBlockedForAppointmentId, notBlockedForGroupId } to hide
  // hosts blacklisted by the real estate of that appointment or of any appointment of that group
  async fetchHostsBySearchText({dispatch, commit }, payload) {
    const { text, ...filters } = payload !== null && typeof payload === 'object' ? payload : { text: payload }
    if (!text) return

    commit('setIsSearchingHosts', true)

    const params = { search: text, ...filters }

    hostService
      .findAll(params)
      .then(({ data }) => {
        const { result } = data
        commit('setSearchTextHosts', result)
        commit('setHostSearchResults', result)
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
      .finally(() => {
        commit('setIsSearchingHosts', false)
      })
  },

  async fetchHostHistoryList({ rootState, dispatch, commit }, hostId) {
    commit('setIsLoading', true)

    const defaultParams = {
      size: rootState.paginate.itemsPerPage,
      page: rootState.paginate.currentPage - 1,
    }

    const params = { ...defaultParams, hostId: hostId }

    await hostService
      .listHostHistory(params)
      .then(({ data }) => {
        const { result, total } = data
        commit('setHostHistoryList', result)
        commit('setTotalPages', total)
      })
      .catch(({ data }) => {
         data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

  async createHostHistory({ dispatch, commit }, body) {
    commit('setIsLoading', true)

    await hostService
      .createHostHistory(body)
      .then(() => {
        dispatch('fetchHostHistoryList', body.host_id)
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

  async fetchSpecialties({ dispatch, commit }, { search = '' } = {}) {
    commit('setIsLoading', true)

    await hostService
      .listSpecialties({ search })
      .then(({ data }) => {
        commit('setSpecialties', data)
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

  async fetchHostSpecialties({ dispatch, commit }, hostId) {
    commit('setIsLoading', true)

    await hostService
      .listHostSpecialties(hostId)
      .then(({ data }) => {
        commit('setHostSpecialties', data)
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },


  async createSpecialty({ dispatch, commit }, { name }) {
    commit('setIsLoading', true)

    return hostService
      .createSpecialty({ name })
      .then(({ data }) => {
        dispatch('fetchSpecialties', { search: '' })
        dispatch(
          'showSuccessMessage',
          Vue.i18n.translate('host.message.specialtyCreated')
        )
        return data
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },


  async deleteSpecialty({ dispatch, commit }, id) {
    commit('setIsLoading', true)

    return hostService
      .deleteSpecialty(id)
      .then(() => {
        return true
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data?.message)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

  async updateHostSpecialties(state, { hostId, specialties }) {
    return hostService.updateHostSpecialties(hostId, { specialties })
  },

  async fetchHostRealEstateBlacklist({ dispatch, commit }, hostId) {
    commit('setIsLoading', true)

    await hostService
      .getHostRealEstateBlacklist(hostId)
      .then(({ data }) => {
        commit('setHostRealEstateBlacklist', data)
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

  async addToHostRealEstateBlacklist({ dispatch, commit }, payload) {
    commit('setIsLoading', true)

    await hostService
      .addToHostRealEstateBlacklist(payload)
      .then(() => {
        dispatch('fetchHostRealEstateBlacklist', payload.host_id)
        dispatch('showSuccessMessage', Vue.i18n.translate('host.realEstateBlacklist.addSuccess'))
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

  async removeFromHostRealEstateBlacklist({ dispatch, commit }, { id, hostId }) {
    commit('setIsLoading', true)

    await hostService
      .removeFromHostRealEstateBlacklist(id)
      .then(() => {
        dispatch('fetchHostRealEstateBlacklist', hostId)
        dispatch('showSuccessMessage', Vue.i18n.translate('host.realEstateBlacklist.removeSuccess'))
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

}

const mutations = {
  setHosts: (state, hosts) => {
    state.hosts = hosts
  },
  setHost: (state, host) => {
    state.host = host
  },
  setHostAccessControlHistory: (state, hostAccessControlHistory) => {
    state.hostAccessControlHistory = hostAccessControlHistory
  },
  setHostServiceTypes: (state, serviceTypes) => {
    state.hostServiceTypes = serviceTypes
  },
  setCreateHostSuccess: (state, success) => {
    state.createHostSuccess = success
  },
  setHostAppointments: (state, hostAppointments) => {
    state.hostAppointments = hostAppointments
  },
  setSearchTextHosts: (state, searchTextHosts) => {
    state.searchTextHosts = searchTextHosts
  },
  setHostPayments: (state, payments) => {
    state.hostPayments = payments
  },
  setIsLoadingChangeStatus: (state, isLoadingChangeStatus) => {
    state.isLoadingChangeStatus = isLoadingChangeStatus;
  },

  setIsSearchingHosts: (state, status) => {
    state.isSearchingHosts = status
  },

  setHostSearchResults: (state, hosts) => {
    state.hostSearchResults = hosts
  },

  setHostHistoryList: (state, hostHistoryList) => {
    state.hostHistoryList = hostHistoryList
  },

  setHostLoading: (state, status) => {
    state.hostLoading = status
  },

  setSpecialties: (state, specialties) => {
    state.specialties = specialties
  },

  setHostSpecialties: (state, hostSpecialties) => {
    state.hostSpecialties = hostSpecialties
  },

  setHostRealEstateBlacklist: (state, list) => {
    state.hostRealEstateBlacklist = list
  },
}

export default {
  state,
  getters,
  actions,
  mutations,
}
